import { formatLengthToArchitecturalString } from '@paragon/client-tools';
import { AnalysisSet, ChordBracingSettings, ComponentDesign, DesignStandard } from '@paragon/contract-library';
import { distanceLessThan } from '@paragon/geometry-library';
import { getPlateSizeString } from '@paragon/material-lists';
import { calculateMemberOverallLength } from '@paragon/three-shared';
import { assertExhaustiveSwitch } from '@paragon/utilities';

import { getComponentInventoryStockLengths } from '../selectors/getComponentInventoryStockLengths';
import { recordWarningDuringAnalysis } from './longRunningAnalysisActions';
import { ThunkActionReturn, ThunkActionVoid } from './thunkActions';

export const warnIfComponentIsNotBuildable =
  (componentDesign: ComponentDesign): ThunkActionVoid =>
  (dispatch) => {
    const warning = dispatch(validateComponentBuildability(componentDesign));
    if (warning) {
      dispatch(recordWarningDuringAnalysis(componentDesign.guid, warning));
    }
  };

export const validateComponentBuildability =
  (componentDesign: ComponentDesign): ThunkActionReturn<string | undefined> =>
  (dispatch, getState) => {
    const tooLongMembers = [];

    const stockLengths = getComponentInventoryStockLengths(getState());
    if (!stockLengths) {
      return;
    }

    for (const member of componentDesign.component.members) {
      const stockLengthOptions = stockLengths.getStockLengths(componentDesign, member.lumber);
      const memberLength = calculateMemberOverallLength(member, componentDesign.component);

      if (!stockLengthOptions || !memberLength) {
        console.warn(`Could not validate member length: ${member.name}.`);
        continue;
      }

      const longestStockLength = Math.max(...stockLengthOptions);
      if (distanceLessThan(longestStockLength, memberLength)) {
        tooLongMembers.push(member.name);
      }
    }

    if (tooLongMembers.length === 1) {
      return `Member ${tooLongMembers[0]} is longer than available stock lengths.`;
    }

    if (tooLongMembers.length > 1) {
      return `Some members are longer than available stock lengths: ${tooLongMembers.join(', ')}`;
    }
  };

export const warnIfIllegalUserPurlinSpacing =
  (analysisSet: AnalysisSet): ThunkActionVoid =>
  (dispatch) => {
    const warnIfIllegal = (
      chordBracingSettings: ChordBracingSettings,
      maxPurlinSpacing: number,
      humanReadableChordBracingSettingsIdentifier: 'sloped top chords' | 'flat top chords' | 'bottom chords',
    ) => {
      if (
        !chordBracingSettings.isSheathed &&
        !chordBracingSettings.useMaxPurlinSpacing &&
        chordBracingSettings.purlinSpacing > maxPurlinSpacing
      ) {
        dispatch(
          recordWarningDuringAnalysis(
            analysisSet.componentDesign.guid,
            `User purlin spacing for ${humanReadableChordBracingSettingsIdentifier} is greater than the calculated max purlin spacing.`,
          ),
        );
      }
    };

    if (analysisSet.calculatedSlopedTopChordPurlinSpacing) {
      warnIfIllegal(
        analysisSet.analysisSettings.chordBracing.slopedTopChordBracingSettings,
        analysisSet.calculatedSlopedTopChordPurlinSpacing,
        'sloped top chords',
      );
    }
    if (analysisSet.calculatedFlatTopChordPurlinSpacing) {
      warnIfIllegal(
        analysisSet.analysisSettings.chordBracing.flatTopChordBracingSettings,
        analysisSet.calculatedFlatTopChordPurlinSpacing,
        'flat top chords',
      );
    }
    if (analysisSet.calculatedBottomChordPurlinSpacing) {
      warnIfIllegal(
        analysisSet.analysisSettings.chordBracing.bottomChordBracingSettings,
        analysisSet.calculatedBottomChordPurlinSpacing,
        'bottom chords',
      );
    }
  };

export const warnIfPlatingJsiLimitIsExceeded =
  (analysisSet: AnalysisSet): ThunkActionVoid =>
  (dispatch) => {
    const {
      analysisSettings: { platingJsiLimit },
    } = analysisSet;
    const jointsForWarning = analysisSet.jsiByJoint
      ?.filter(({ jsi }) => jsi <= 1 && jsi > platingJsiLimit)
      .map(({ joint }) => joint);
    if (jointsForWarning?.length) {
      const shouldUsePlural = jointsForWarning.length > 1;
      const sIfPlural = shouldUsePlural ? 's' : '';
      dispatch(
        recordWarningDuringAnalysis(
          analysisSet.componentDesign.guid,
          `The JSI${sIfPlural} of ${shouldUsePlural ? 'these' : 'this'} passing joint${sIfPlural} ` +
            `exceed${shouldUsePlural ? '' : 's'} the limit set in the Analysis Settings: ` +
            `${jointsForWarning.join(', ')}.`,
        ),
      );
    }
  };

export const warnIfSplicePlatesTooSmall =
  (analysisSet: AnalysisSet): ThunkActionVoid =>
  (dispatch) => {
    const roundDistance = (x: number) => Math.round(x * 10) / 10;
    const { splicePlateRecommendations } = analysisSet;
    if (!splicePlateRecommendations || splicePlateRecommendations.length === 0) return;
    const recommendations = splicePlateRecommendations.map((recommendation) =>
      recommendation.distanceToEdge === null
        ? `${recommendation.joint} (${getPlateSizeString({ width: roundDistance(recommendation.width), length: roundDistance(recommendation.length), geometryType: 'Rectangular' })})`
        : `${recommendation.joint} (extend to within ${formatLengthToArchitecturalString(
            recommendation.distanceToEdge,
          )} of chord edges)`,
    );
    dispatch(
      recordWarningDuringAnalysis(
        analysisSet.componentDesign.guid,
        `Some splice plates are smaller than recommended: ${recommendations.join(', ')}`,
      ),
    );
  };

// A TDD note is also generated for this by AnalysisReportBuilder.GetCreepFactorWarningOrNull in DesignNotes.cs.
export const warnIfDeflectionCreepFactorTooLow =
  (analysisSet: AnalysisSet): ThunkActionVoid =>
  (dispatch) => {
    const { designStandard, useWetServiceFactor, creepFactor } = analysisSet.analysisSettings;

    const minCreepFactor = (() => {
      switch (designStandard) {
        case DesignStandard.Unknown:
        case DesignStandard.Ansi95:
          return null;
        case DesignStandard.Tpi2002:
        case DesignStandard.Tpi2007:
          return !useWetServiceFactor ? 1.5 : 2.0;
        case DesignStandard.Tpi2014:
        case DesignStandard.Tpi2022:
          return !useWetServiceFactor ? 2.0 : 3.0;
        default:
          assertExhaustiveSwitch(designStandard);
      }
    })();
    if (minCreepFactor === null) {
      return;
    }

    if (creepFactor < minCreepFactor) {
      const source = (() => {
        switch (designStandard) {
          case DesignStandard.Unknown:
          case DesignStandard.Ansi95:
            return null;
          case DesignStandard.Tpi2002:
            return 'the NDS';
          case DesignStandard.Tpi2007:
            return 'TPI 1-2007';
          case DesignStandard.Tpi2014:
            return 'TPI 1-2014';
          case DesignStandard.Tpi2022:
            return 'TPI 1-2022';
          default:
            assertExhaustiveSwitch(designStandard);
        }
      })();

      const wetServiceStr: string = !useWetServiceFactor ? '' : ' for wet service';

      dispatch(
        recordWarningDuringAnalysis(
          analysisSet.componentDesign.guid,
          `The deflection creep factor is less than the minimum value of ${minCreepFactor}${wetServiceStr} specified in ${source}.`,
        ),
      );
    }
  };

/**
 * Envelope procedure is supposed to be only applied when the building is Low-Rise,
 * (as defined in ASCE 7 section 26.2)
 */
export function warnIfEnvelopeProcedureUsedOnHighRise(analysisSet: AnalysisSet): ThunkActionVoid {
  return (dispatch) => {
    const { useWind, windDesignMethod, buildingWidth, buildingLength, meanRoofHeight } = analysisSet.loadSettings;
    const isLowRise = meanRoofHeight <= Math.min(buildingWidth, buildingLength, 60);
    const isEnvelopeProcedure =
      windDesignMethod === 'WindAsceMwfrsEnvelope' || windDesignMethod === 'WindAsceHybridEnvelope';

    if (useWind && isEnvelopeProcedure && !isLowRise) {
      dispatch(
        recordWarningDuringAnalysis(
          analysisSet.componentDesign.guid,
          `An Envelope Procedure Wind Design Method has been applied, but the building is not low-rise according to the wind load settings. Consult your building code, and consider using the Directional Procedure instead.`,
        ),
      );
    }
  };
}
